import {
  ReportsService,
  ConversationRetryRequest,
  ConversationExportRequest,
  ConversationCreateResponse,
  ConversationFeedbackRequest,
} from "@/api-client";
import {
  handleErrorResponse,
  handleGenericCatchError,
} from "@/utils/functions";
import { FC, useState } from "react";
import { Message } from "./AiChatBox";
import { IoReload } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";
import { MessageSquare, ThumbsUp, Loader2 } from "lucide-react";
import { ModalCard } from "@/components/sharedComponent/ModalCard";
import { ModalHeader } from "@/components/sharedComponent/ModalHeader";

interface AiMessageProps {
  message: Message;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  convResponse: ConversationCreateResponse | undefined;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setTempStatus: (status: { status: string; color: string } | null) => void;
  updateRemainingQuota: (newQuota: number | undefined) => void;
}

export const AssistantMessage: FC<AiMessageProps> = ({
  message,
  isLoading,
  setMessages,
  textareaRef,
  convResponse,
  setIsLoading,
  setTempStatus,
  updateRemainingQuota,
}) => {
  const [isLikeSent, setIsLikeSent] = useState<boolean>(false);
  const [feedbackComment, setFeedbackComment] = useState<string>("");
  const [isLikeLoading, setIsLikeLoading] = useState<boolean>(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState<boolean>(false);
  const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState<boolean>(false);
  const [showFeedbackMessage, setFeedbackMessage] = useState<boolean>(false);

  const handleRetryMessage = async (
    retryIdentifier: string | null | undefined
  ) => {
    if (isLoading || !convResponse || !retryIdentifier) return;

    try {
      setIsLoading(true);
      const body: ConversationRetryRequest = {
        sessionId: convResponse.sessionId,
        retryIdentifier,
      };

      setMessages((prevMessages) => {
        const lastIndex = prevMessages
          .map((msg, index) =>
            msg.role === "assistant" && msg.retryIdentifier === retryIdentifier
              ? index
              : -1
          )
          .filter((index) => index !== -1)
          .pop();

        if (lastIndex !== undefined) {
          return prevMessages.filter((_, index) => index !== lastIndex);
        }

        return prevMessages;
      });

      const { data, error } = await ReportsService.aiConversationMessageRetry({
        body,
      });

      const message = data?.data;

      if (error) {
        handleErrorResponse(error);
        return;
      }

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          ...message,
          role: "assistant",
          sessionId: message?.sessionId || convResponse.sessionId,
          messageId: message?.messageId || `assistant-${Date.now()}`,
        },
      ]);
      updateRemainingQuota(message?.remainingQuota || undefined);

      setTempStatus(null);
    } catch (error) {
      handleGenericCatchError(error);
    } finally {
      setIsLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const handleFeedbackMessage = (messageId: string) => {
    if (isFeedbackSent) return;
    if (showFeedbackMessage) {
      setFeedbackMessage(false);
      setFeedbackComment("");
    } else {
      setFeedbackMessage(true);
      setFeedbackComment("");
    }
  };

  const handleSubmitFeedback = async (
    messageId: string,
    isHelpful: boolean,
    isLikeAction: boolean = false
  ) => {
    if (isLikeAction) {
      if (isLikeSent || isLikeLoading) return;
      setIsLikeLoading(true);
    } else {
      if (isFeedbackSent || isFeedbackLoading || !convResponse) return;
      setIsFeedbackLoading(true);
    }

    try {
      if (!convResponse) return;

      const body: ConversationFeedbackRequest = {
        sessionId: convResponse?.sessionId,
        messageId,
        isHelpful,
        comments: isLikeAction ? null : feedbackComment,
      };

      const { error } = await ReportsService.aiConversationMessageFeedback({
        body,
      });

      if (error) {
        handleErrorResponse(error);
        return;
      }

      if (isLikeAction) {
        setIsLikeSent(true);
      } else {
        setIsFeedbackSent(true);
      }
    } catch (error) {
      handleGenericCatchError(error);
    } finally {
      if (isLikeAction) {
        setIsLikeLoading(false);
      } else {
        setIsFeedbackLoading(false);
        setFeedbackMessage(false);
        setFeedbackComment("");
      }
    }
  };

  const handleExportData = async (messageId: string | null | undefined) => {
    try {
      if (!convResponse || !messageId) return;
      setIsLoadingExport(true);
      const body: ConversationExportRequest = {
        sessionId: convResponse?.sessionId,
        messageId,
      };

      const { error } = await ReportsService.aiConversationMessageExport({
        body,
      });

      if (error) {
        handleErrorResponse(error);
        return;
      }
    } catch (error) {
      handleGenericCatchError(error);
    } finally {
      setIsLoadingExport(false);
    }
  };

  const { errorMessage, retryIdentifier, queryResult, color } = message;

  if (errorMessage) {
    return (
      <div>
        <div className="flex items-start gap-2 rounded-lg border border-l-4 border-orange-200 border-l-orange-400 bg-orange-50 p-3">
          <div className="text-sm text-orange-800">{errorMessage}</div>
        </div>
        {retryIdentifier && (
          <button
            onClick={() => handleRetryMessage(retryIdentifier)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            <IoReload className="group-hover:animate-[spin_0.5s_linear]" />
            Retry
          </button>
        )}
      </div>
    );
  }

  const { title, type, data, messages } = queryResult || {};
  const isTable = type === "table";
  const isValue = type === "value";

  const colorClasses: Record<
    string,
    { bg: string; border: string; text: string; borderL: string }
  > = {
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-800",
      borderL: "border-l-blue-400",
    },
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-800",
      borderL: "border-l-green-400",
    },
    yellow: {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      text: "text-yellow-800",
      borderL: "border-l-yellow-400",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-800",
      borderL: "border-l-red-400",
    },
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-800",
      borderL: "border-l-purple-400",
    },
    orange: {
      bg: "bg-orange-50",
      border: "border-orange-200",
      text: "text-orange-800",
      borderL: "border-l-orange-400",
    },
    gray: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-800",
      borderL: "border-l-gray-400",
    },
  };

  const currentColor = colorClasses[color || "blue"] || colorClasses.blue;

  const renderTableData = (data: string | null | undefined) => {
    try {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        const headers = Object.keys(parsedData[0]);
        return (
          <div className="mt-3 max-h-[60vh] overflow-x-auto overflow-y-auto rounded-lg border border-gray-200">
            <table className="min-w-full">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow">
                <tr>
                  {headers.map((header) => (
                    <th
                      key={header}
                      className="bg-gray-50 px-4 py-2 text-left text-xs font-medium tracking-wider text-gray-700"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {parsedData.map((row: any, idx: number) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {headers.map((header) => (
                      <td
                        key={header}
                        className="whitespace-nowrap px-4 py-2 text-sm text-gray-900"
                      >
                        {typeof row[header] === "object" && row[header] !== null
                          ? Object.keys(row[header]).length === 0
                            ? ""
                            : JSON.stringify(row[header])
                          : row[header]?.toString() || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    } catch (e) {
      return <p>Error rendering table data</p>;
    }
    return null;
  };

  return (
    <div className="space-y-2">
      {title && <div className="font-semibold text-gray-900">{title}</div>}

      {isTable && renderTableData(data)}

      {isValue && data && (
        <div
          className={`rounded-lg ${currentColor.bg} p-3 text-sm font-medium ${currentColor.text}`}
        >
          {data}
        </div>
      )}

      {!isTable && !isValue && data && (
        <div className="text-sm text-gray-900">{data}</div>
      )}

      {messages && messages.length > 0 && (
        <div
          className={`space-y-1 rounded-lg border border-l-4 ${currentColor.border} ${currentColor.borderL} ${currentColor.bg} p-3`}
        >
          {messages.map((msg, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className={`text-sm ${currentColor.text}`}> {msg}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!isFeedbackSent && (
            <button
              onClick={() =>
                handleSubmitFeedback(message.messageId, true, true)
              }
              disabled={isLikeSent || isLikeLoading}
              className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-sm transition-colors ${
                isLikeSent
                  ? "cursor-not-allowed border-green-500 bg-green-500 text-white shadow-sm"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-blue-600"
              } disabled:cursor-not-allowed`}
            >
              {isLikeLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ThumbsUp className="h-4 w-4" />
              )}
            </button>
          )}
          {!isLikeSent && (
            <button
              onClick={() => handleFeedbackMessage(message.messageId)}
              disabled={isFeedbackSent}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                showFeedbackMessage
                  ? "border-green-500 bg-green-50 text-green-700"
                  : isFeedbackSent
                    ? "cursor-not-allowed border-green-500 bg-green-500 text-white shadow-sm"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-green-600"
              } disabled:cursor-not-allowed`}
            >
              <MessageSquare className="h-4 w-4" />
              <span>{isFeedbackSent ? "Feedback sent" : "Feedback"}</span>
            </button>
          )}
          {message.queryResult?.exportable && (
            <button
              onClick={() => handleExportData(message.messageId)}
              disabled={isLoadingExport}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-blue-600 disabled:cursor-not-allowed"
            >
              {isLoadingExport ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MdOutlineFileDownload className="h-4 w-4" />
              )}
              <span>Download</span>
            </button>
          )}
        </div>
      </div>

      <ModalCard showModal={showFeedbackMessage}>
        <div className="w-[450px] max-w-[90vw]">
          <ModalHeader
            title="Leave Feedback"
            subtitle=""
            colorTheme="blue"
            onClose={() => {
              setFeedbackMessage(false);
              setFeedbackComment("");
            }}
            onSave={() => handleSubmitFeedback(message.messageId, false)}
            isLoading={isFeedbackLoading}
          />
          <div className="rounded-b-lg bg-white p-4">
            <textarea
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="Leave your feedback here..."
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
              rows={4}
              autoFocus
            />
          </div>
        </div>
      </ModalCard>
    </div>
  );
};
